import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Chip, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { AccessTime as AccessTimeIcon, ChatBubbleOutline as ChatBubbleIcon, ErrorOutline as ErrorOutlineIcon } from '@mui/icons-material';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { COUPON_CHIP_THEME, TALK_ROOM_THEME, TalkRoom } from '../../types/talk';
import { getExternalTalkRoomUrl } from '../../utils/urlUtils';
import { formatDateWithTime, getHumanReadableTimeAgo } from '../../utils/dateUtils';
import TalkStatusChip from './TalkStatusChip';
import ElapsedDaysDisplay from './ElapsedDaysDisplay';
import { ExternalLinkButton } from '../common';
import { NURSE_TYPE_THEME } from '../../types/nurse';
import { PATIENT_THEME } from '../../types/patient';

interface TalkRoomTableRowProps {
  room: TalkRoom;
  onClick: () => void;
}

const TalkRoomTableRow: React.FC<TalkRoomTableRowProps> = ({ room, onClick }) => {
  const hasSessions = room.sessions && room.sessions.length > 0;
  return (
    <>
      <TableRow
        hover
        onClick={onClick}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          },
          borderBottom: hasSessions ? 'none' : undefined
        }}
      >
        {/* ステータス列 */}
        <TableCell sx={{ minWidth: '200px', width: '200px' }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            justifyContent: 'flex-start'
          }}>
            <TalkStatusChip
              status={room.talk_status}
              isForceEnd={room.is_force_end}
              tooltipContent={
                room.talk_status === 'APPLYING' && room.request_at
                  ? `申込み日時: ${getHumanReadableTimeAgo(room.request_at)} ( ${formatDateWithTime(room.request_at, 'jp')} )`
                  : undefined
              }
            />
          </Box>
        </TableCell>

        {/* 看護師情報 */}
        <TableCell>
          {room.nurse ? (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                {room.nurse.name}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.85rem', mt: 0.5 }}>
                ニックネーム: <span style={{ fontWeight: 'bold' }}>{room.nurse.nickname}</span>
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              [未設定]
            </Typography>
          )}
        </TableCell>

        {/* 患者情報 */}
        <TableCell>
          {room.patient ? (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                {room.patient.name}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.85rem', mt: 0.5 }}>
                ニックネーム: <span style={{ fontWeight: 'bold' }}>{room.patient.nickname}</span>
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              [未設定]
            </Typography>
          )}
        </TableCell>

        {/* プラン列 */}
        <TableCell>
          {hasSessions ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {room.sessions?.map((session, index) => (
                <Box
                  key={session.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    p: 1,
                    minHeight: '40px'
                  }}
                >
                  <Chip
                    label={session.price && session.price > 0 ? `${session.price.toLocaleString()}円` : '無料'}
                    size="small"
                    color={session.price && session.price > 0 ? 'error' : 'default'}
                    variant="outlined"
                    sx={{
                      ...(session.price && session.price > 0 ? {
                        fontWeight: 'bold',
                        borderColor: 'error.main',
                        color: 'error.main'
                      } : {})
                    }}
                  />
                  {/* クーポンアイコン表示 */}
                  {session.coupon_id && (
                    <Tooltip
                      title={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {session.coupon_name ? `${session.coupon_name}（${session.coupon_code}）` : session.coupon_code}
                          </Typography>
                          {session.coupon_discount_amount && (
                            <Typography variant="body2">
                              割引額: ¥{session.coupon_discount_amount.toLocaleString()}
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'grey.300' }}>
                            クーポン詳細ページへ
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <Box
                        component={RouterLink}
                        to={`/coupons/${session.coupon_id}/usages`}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textDecoration: 'none',
                          backgroundColor: COUPON_CHIP_THEME.BACKGROUND,
                          color: COUPON_CHIP_THEME.COLOR,
                          border: `1px solid ${COUPON_CHIP_THEME.BORDER}`,
                          borderRadius: '50%',
                          padding: '2px',
                          width: '24px',
                          height: '24px',
                          '&:hover': {
                            backgroundColor: COUPON_CHIP_THEME.BACKGROUND_HOVER,
                          }
                        }}
                      >
                        <LocalOfferOutlinedIcon sx={{ fontSize: 16 }}/>
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ p: 1 }}>
              <Chip
                label={room.price && room.price > 0 ? `${room.price.toLocaleString()}円` : '無料'}
                size="small"
                color={room.price && room.price > 0 ? 'error' : 'default'}
                variant="outlined"
                sx={{
                  ...(room.price && room.price > 0 ? {
                    fontWeight: 'bold',
                    borderColor: 'error.main',
                    color: 'error.main'
                  } : {})
                }}
              />
            </Box>
          )}
        </TableCell>

        {/* 期間列 */}
        <TableCell align="center" sx={{ width: '200px' }}>
          {hasSessions ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
              {room.sessions?.map((session, index) => (
                <Box
                  key={session.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    p: 1,
                    minHeight: '40px'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.85rem' }}>
                    {session.start_at ? (
                      <>
                        <Box>
                          {`${new Date(session.start_at).getFullYear()}/${new Date(session.start_at).getMonth() + 1}/${new Date(session.start_at).getDate()}`}
                        </Box>
                        <Box component="span" sx={{ color: 'text.secondary', mx: 0.5 }}>〜</Box>
                        <Box>
                          {session.end_at ? `${new Date(session.end_at).getFullYear()}/${new Date(session.end_at).getMonth() + 1}/${new Date(session.end_at).getDate()}` : '継続中'}
                        </Box>
                      </>
                    ) : (
                      <Box sx={{ color: 'text.secondary' }}>-</Box>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, p: 1, minHeight: '40px' }}>
              {room.start_at ? (
                <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.85rem' }}>
                  <Box>
                    {`${new Date(room.start_at).getFullYear()}/${new Date(room.start_at).getMonth() + 1}/${new Date(room.start_at).getDate()}`}
                  </Box>
                  <Box component="span" sx={{ color: 'text.secondary', mx: 0.5 }}>〜</Box>
                  <Box>
                    {room.end_at ? `${new Date(room.end_at).getFullYear()}/${new Date(room.end_at).getMonth() + 1}/${new Date(room.end_at).getDate()}` : '継続中'}
                  </Box>
                </Box>
              ) : (
                <Box sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>-</Box>
              )}
            </Box>
          )}
        </TableCell>

        {/* 経過日数 */}
        <TableCell align="center">
          <ElapsedDaysDisplay
            startDate={room.start_at}
            endDate={room.end_at}
            status={room.talk_status}
          />
        </TableCell>

        {/* 最終チャット */}
        <TableCell align="center" sx={{ minWidth: '200px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            {room.talk_status !== 'CANCELED' && (
              <ExternalLinkButton
                href={getExternalTalkRoomUrl(room.id)}
                size="small"
                variant="outlined"
                color="primary"
                buttonType="chat"
                themeColor={{
                  main: TALK_ROOM_THEME.DARK,
                  hover: `${TALK_ROOM_THEME.BACKGROUND}99` // 透明度を追加
                }}
                onClick={(e) => { e.stopPropagation(); }}
                sx={{ mb: 1 }}
              />
            )}

            {room.last_chat_send_at ? (() => {
              // 経過時間を一度だけ計算
              const hoursElapsed = room.talk_status === 'DOING'
                ? (new Date().getTime() - new Date(room.last_chat_send_at).getTime()) / (1000 * 60 * 60)
                : 0;
              // 経過時間による警告レベルの定義
              const WARNING_THRESHOLD_HOURS = 24;  // 24時間経過で注意
              const CRITICAL_THRESHOLD_HOURS = 36; // 36時間経過で警告
              // 警告レベルの判定
              const alertLevel = room.talk_status === 'DOING'
                ? hoursElapsed >= CRITICAL_THRESHOLD_HOURS ? 'critical'
                  : hoursElapsed >= WARNING_THRESHOLD_HOURS ? 'warning'
                    : 'normal'
                : 'normal';
              // 警告レベルごとのスタイル定義
              const alertStyles = {
                critical: {
                  // 36時間以上経過: 赤系の強い警告
                  boxStyle: {
                    backgroundColor: '#ffebee',    // 薄い赤色の背景
                    border: '2px solid #f44336',   // 赤色の太いボーダー
                    boxShadow: '0 0 0 2px rgba(244, 67, 54, 0.3)', // 赤色のシャドウ
                    borderRadius: '8px',
                    padding: '4px 8px',
                    transform: 'scale(1.05)',
                    transition: 'all 0.3s ease-in-out'
                  },
                  textColor: '#d32f2f',          // 濃い赤色のテキスト
                  iconColor: '#d32f2f',          // 濃い赤色のアイコン
                  showWarningIcon: true,
                  fontWeight: 'bold' as const
                },
                warning: {
                  // 24-36時間経過: 黄色系の注意喚起
                  boxStyle: {
                    backgroundColor: '#fff8e1',    // 薄い黄色の背景
                    border: '1px solid #ffc107',   // 黄色のボーダー
                    borderRadius: '6px',
                    padding: '4px 8px'
                  },
                  textColor: '#f57c00',          // オレンジ色のテキスト
                  iconColor: '#f57c00',          // オレンジ色のアイコン
                  showWarningIcon: true,
                  fontWeight: 'bold' as const
                },
                normal: {
                  // 24時間未満: 通常表示
                  boxStyle: {},
                  textColor: 'text.secondary',
                  iconColor: '#666',
                  showWarningIcon: false,
                  fontWeight: 'normal' as const
                }
              };
              const currentStyle = alertStyles[alertLevel];
              return (
                <Tooltip
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                        {formatDateWithTime(room.last_chat_send_at, 'jp')}
                      </Typography>
                      {room.last_chat_message && (
                        <Typography variant="body2">
                          {room.last_chat_message}
                        </Typography>
                      )}
                    </Box>
                  }
                  arrow
                  placement="left-start"
                  sx={{ maxWidth: 800 }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      cursor: 'pointer'
                    }}
                  >
                    {/* 最終メッセージ送信者表示と吹き出しアイコン */}
                    {room.last_chat_send_user_id && room.nurse && room.patient && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip
                          label={room.last_chat_send_user_id === room.nurse.user_id ? '看護師' : '患者'}
                          size="small"
                          sx={{
                            height: '18px',
                            fontSize: '0.7rem',
                            backgroundColor: room.last_chat_send_user_id === room.nurse.user_id
                              ? NURSE_TYPE_THEME.NORMAL.BACKGROUND
                              : PATIENT_THEME.MYSELF.BACKGROUND,
                            color: room.last_chat_send_user_id === room.nurse.user_id
                              ? NURSE_TYPE_THEME.NORMAL.DARK
                              : PATIENT_THEME.MYSELF.DARK,
                            border: room.last_chat_send_user_id === room.nurse.user_id
                              ? `1px solid ${NURSE_TYPE_THEME.NORMAL.BORDER}`
                              : `1px solid ${PATIENT_THEME.MYSELF.BORDER}`,
                            fontWeight: 'normal'
                          }}
                        />

                        {/* メッセージ内容表示アイコン */}
                        {room.last_chat_message && (
                          <ChatBubbleIcon
                            fontSize="small"
                            sx={{
                              color: 'text.secondary',
                              opacity: 0.7,
                              fontSize: '0.9rem',
                              '&:hover': {
                                opacity: 1
                              }
                            }}
                          />
                        )}
                      </Box>
                    )}

                    {/* 時間表示 */}
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      // 警告レベルに応じたスタイルを適用
                      ...currentStyle.boxStyle
                    }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: currentStyle.textColor,
                          fontWeight: currentStyle.fontWeight
                        }}
                      >
                        {getHumanReadableTimeAgo(room.last_chat_send_at)}
                      </Typography>
                      {/* 警告アイコンまたは時計アイコンの表示 */}
                      {currentStyle.showWarningIcon ? (
                        <ErrorOutlineIcon
                          sx={{
                            fontSize: '18px',
                            color: currentStyle.iconColor
                          }}
                        />
                      ) : (
                        <AccessTimeIcon
                          fontSize="small"
                          sx={{
                            fontSize: '0.9rem',
                            opacity: 0.7,
                            color: 'action',
                            '&:hover': {
                              opacity: 1
                            }
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Tooltip>
              );
            })() : (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            )}
          </Box>
        </TableCell>
      </TableRow>
    </>
  );
};
export default TalkRoomTableRow;
